<template>
  <SplashWrapper
    :hide-buttons="true"
    :hide-logo="isExpert"
    title="Set up two-factor authentication"
    :narrow-page="true"
  >
    <iColumn v-if="forcedEnrollBackupCodes.length > 0" gap="extraLarge">
      <iText variant="subtle">
        Two-factor authentication is on. Save these one-time backup codes somewhere safe —
        they are your only way in if you lose your other methods, and they will not be shown again.
      </iText>
      <iColumn gap="small" align="center">
        <iText
          v-for="backupCode in forcedEnrollBackupCodes"
          :key="backupCode"
          font-weight="dark"
        >
          {{ backupCode }}
        </iText>
      </iColumn>
      <iButton
        variant="primary"
        type="button"
        width="fill"
        @click="finishForcedEnroll"
      >
        I've saved my backup codes — continue
      </iButton>
    </iColumn>

    <iColumn v-else-if="method === null" gap="extraLarge">
      <iText variant="subtle">
        Your account requires two-factor authentication. Choose how you'd like to secure it.
      </iText>
      <iColumn>
        <iButton
          variant="primary"
          type="button"
          width="fill"
          left-icon="mobile"
          @click="chooseTotp"
        >
          Use an authenticator app
        </iButton>
        <iButton
          variant="secondary"
          type="button"
          width="fill"
          left-icon="key"
          @click="choosePasskey"
        >
          Use a passkey
        </iButton>
        <iText v-if="twoFactorErrorMessage" variant="error">
          {{ twoFactorErrorMessage }}
        </iText>
      </iColumn>
    </iColumn>

    <iColumn v-else-if="method === 'totp'" gap="extraLarge">
      <iRow>
        <iText variant="subtle">
          Scan the QR code with an authenticator app, then enter the 6-digit code to finish signing in.
        </iText>
      </iRow>

      <iColumn gap="standard" align="center">
        <iImageContainer
          v-if="forcedEnrollQrDataUri"
          :src="forcedEnrollQrDataUri"
          alt="Authenticator QR code"
          :width="200"
          :height="200"
        />
        <iText v-if="forcedEnrollSecret" variant="subtle" font-size="small">
          Or enter this key manually:
        </iText>
        <iText v-if="forcedEnrollSecret" font-weight="dark">
          {{ forcedEnrollSecret }}
        </iText>
      </iColumn>

      <iColumn>
        <iTextInput
          ref="code"
          v-model="code"
          :focus="true"
          label-position="above"
          label="Authentication code: "
          width="fill"
          name="totp-code"
          inputmode="numeric"
          autocomplete
          @enter="submitConfirm"
        />
        <iText v-if="twoFactorErrorMessage" variant="error">
          {{ twoFactorErrorMessage }}
        </iText>
        <iButton
          variant="primary"
          type="submit"
          width="fill"
          :disabled="hasInvalidCode"
          :is-loading="isLoading"
          loading-spinner-position="inside"
          @click="submitConfirm"
        >
          Verify and continue
        </iButton>
        <iButton
          variant="tertiary"
          type="button"
          width="fill"
          @click="resetChoice"
        >
          Choose a different method
        </iButton>
      </iColumn>
    </iColumn>

    <iColumn v-else gap="extraLarge">
      <iText variant="subtle">
        Give this passkey a name, then follow your browser's prompt to register it (Face ID, Touch ID,
        or a security key such as a YubiKey).
      </iText>
      <iColumn>
        <iTextInput
          v-model="passkeyAlias"
          :focus="true"
          label-position="above"
          label="Passkey name"
          placeholder="e.g. MacBook Touch ID, YubiKey"
          width="fill"
          @enter="submitPasskey"
        />
        <iText v-if="twoFactorErrorMessage" variant="error">
          {{ twoFactorErrorMessage }}
        </iText>
        <iButton
          variant="primary"
          type="button"
          width="fill"
          :is-loading="isLoading"
          loading-spinner-position="inside"
          @click="submitPasskey"
        >
          Register passkey and continue
        </iButton>
        <iButton
          variant="tertiary"
          type="button"
          width="fill"
          @click="resetChoice"
        >
          Choose a different method
        </iButton>
      </iColumn>
    </iColumn>
  </SplashWrapper>
</template>

<script>
import { mapActions, mapState, mapWritableState } from "pinia";
import { useUserStore } from "@/stores/user-store";
import SplashWrapper from "@/components/splash/SplashWrapper";

export default {
  name: "TwoFactorEnrollForm",
  components: { SplashWrapper },
  inject: ["intent"],
  data() {
    return {
      method: null,
      code: "",
      passkeyAlias: "",
    };
  },
  computed: {
    ...mapWritableState(useUserStore, ["twoFactorErrorMessage"]),
    ...mapState(useUserStore, [
      "isLoading",
      "forcedEnrollSecret",
      "forcedEnrollQrDataUri",
      "forcedEnrollBackupCodes",
    ]),
    hasInvalidCode() {
      return this.code.replace(/\D/g, "").length < 6;
    },
    isExpert() {
      return this.intent === "expert";
    },
  },
  mounted() {
    this.twoFactorErrorMessage = "";
  },
  methods: {
    ...mapActions(useUserStore, [
      "enrollForcedTotp",
      "confirmForcedTotp",
      "forcedRegisterPasskey",
      "finishForcedEnroll",
    ]),
    chooseTotp() {
      this.twoFactorErrorMessage = "";
      this.method = "totp";
      this.enrollForcedTotp();
    },
    choosePasskey() {
      this.twoFactorErrorMessage = "";
      this.method = "passkey";
    },
    resetChoice() {
      this.method = null;
      this.code = "";
      this.passkeyAlias = "";
      this.twoFactorErrorMessage = "";
    },
    submitConfirm() {
      if (this.hasInvalidCode) {
        return;
      }
      this.confirmForcedTotp(this.code);
    },
    submitPasskey() {
      this.forcedRegisterPasskey(this.passkeyAlias || "Passkey");
    },
  },
};
</script>

<style lang="scss" scoped></style>
